import { CommonModule } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Injector, ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActionReducer, MetaReducer, StoreModule } from '@ngrx/store';
import { ServiceLocator } from './ServiceLocator';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BsDatepickerModule,BsLocaleService  } from 'ngx-bootstrap/datepicker';
import {
  CtBodyComponent,
  CtButtonComponent,
  CtColumnComponent,
  CtEmailFormComponent,
  CtHeaderComponent,
  CtPanelComponent,
  CtRowComponent,
  CtViewComponent,
  CtPreviewComponent,
  CtBarFilterComponent,
  CtInputTextComponent,
  CtInputNumberComponent,
  CtSelectComponent,
  CtInputCheckBoxComponent,
  CtLabelComponent,
  CtFormComponent,
  CtTabComponent,
  CtContentItemComponent,
  CtBarButtonComponent,
  CtTableComponent,
  CtDialogComponent,
  CtFormListComponent,
  AlertDialogComponent,
  CtMenuComponent,
  CtStepperComponent,
  CtStepperItemComponent,
  CtInputFileComponent,
  CtTextareaComponent,
  CtSidebarComponent,
  CtBannerComponent,
  CtTimelineComponent,
  CtInputDateComponent,
  CtFilterWindowComponent,
  CtFilterContainerComponent,
  CtCustomSelect,
  CtNavbarComponent,
  CtMessageComponent,
  CtMessageContainerComponent,
  CtInputRadioGroupComponent,
  CtRadioComponent,
  CtProgressBarComponent,
  CtRegexComponent,
  CtRadioContainerComponent,
  CtAnchorComponent,
  CtRecaptchaComponent,
  CtOtpComponent,
  CtButtonCompressedContainerComponent,
  CtReceiptComponent,
  CtFooterComponent,
  CtDividerComponent,
  CtButtonUncompressedContainerComponent,
  CtCardComponent
} from './controls';

import {
  UtilService,
  AuthService,
  PaintService,
  HttpService,
  BreadCrumbService,
  ConfirmationService,
  SpinnerService,
  ActionService,
  ComponentService,
  MenuService,
  ViewService,
  StatisticService,
  PlusNotificationService,
  RequestQueueService,
  MessageService,
  PlusMessageService,
  StoreService
} from './services';
import { SafeUrlPipe } from './pipes/safe-url.pipe';
import { DragDropDirective } from './directives/drag-drop.directive';
import { TokenReducer, ViewReducer } from './store/reducers/dataStore.reducer';
import { localStorageSync } from 'ngrx-store-localstorage';
import { ModalModule } from 'ngx-bootstrap/modal';
import { FormatTimePipe } from './pipes/format-time.pipe';
import { PwdValidatorDirective } from './directives';
import { NoCacheHeadersInterceptor } from './interceptors/NoCacheHeadersInterceptor';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { esLocale } from 'ngx-bootstrap/locale';
import { RECAPTCHA_SETTINGS, RecaptchaModule, RecaptchaSettings } from 'ng-recaptcha';

defineLocale('es', esLocale);
const servicesArray = [
  HttpService,
  ActionService,
  ComponentService,
  PaintService,
  UtilService,
  SpinnerService,
  ConfirmationService,
  AuthService,
  BreadCrumbService,
  MenuService,
  ViewService,
  StatisticService,
  PlusNotificationService,
  RequestQueueService,
  MessageService,
  PlusMessageService,
  StoreService
];

const controlsArray = [
  AlertDialogComponent,
  CtButtonComponent,
  CtEmailFormComponent,
  CtViewComponent,
  CtRowComponent,
  CtColumnComponent,
  CtPanelComponent,
  CtHeaderComponent,
  CtBodyComponent,
  CtPreviewComponent,
  CtTableComponent,
  CtBarFilterComponent,
  CtBarButtonComponent,
  CtInputTextComponent,
  CtInputNumberComponent,
  CtSelectComponent,
  CtInputCheckBoxComponent,
  CtLabelComponent,
  CtFormComponent,
  CtTabComponent,
  CtContentItemComponent,
  CtDialogComponent,
  CtFormListComponent,
  CtMenuComponent,
  CtStepperComponent,
  CtStepperItemComponent,
  CtInputFileComponent,
  CtTextareaComponent,
  CtSidebarComponent,
  CtBannerComponent,
  CtTimelineComponent,
  CtInputDateComponent,
  CtFilterWindowComponent,
  CtFilterContainerComponent,
  CtCustomSelect,
  CtNavbarComponent,
  CtMessageComponent,
  CtMessageContainerComponent,
  CtInputRadioGroupComponent,
  CtRadioComponent,
  CtProgressBarComponent,
  CtReceiptComponent,
  CtAnchorComponent,
  CtRecaptchaComponent,
  CtOtpComponent,
  CtButtonCompressedContainerComponent,
  CtButtonUncompressedContainerComponent,
  CtFooterComponent,
  CtDividerComponent,
  CtCardComponent
];

const pipesAndDirectives = [
  SafeUrlPipe,
  DragDropDirective,
  FormatTimePipe,
  PwdValidatorDirective
]

const reducers = {
  loggedUser: TokenReducer,
  currentView: ViewReducer
}

export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync({ keys: ['loggedUser'], rehydrate: true })(reducer);
}
const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

export const options: Partial<IConfig> | (() => Partial<IConfig>) = null;

@NgModule({
  declarations: ([].concat(controlsArray, pipesAndDirectives)),
  imports: [
    HttpClientModule,
    CommonModule,
    RecaptchaModule,
    ReactiveFormsModule,
    FormsModule,
    ModalModule.forRoot(),
    NgxMaskModule.forRoot(),   
    BrowserAnimationsModule,
    BsDatepickerModule.forRoot(),
    StoreModule.forRoot(reducers, {
      metaReducers, runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
        strictStateSerializability: false,
        strictActionSerializability: false,
        strictActionWithinNgZone: true,
        strictActionTypeUniqueness: true,
      },
    })
  ],
  exports: [...controlsArray, ...pipesAndDirectives, StoreModule, NgxMaskModule],
  providers: [...servicesArray, { provide: HTTP_INTERCEPTORS, useClass: NoCacheHeadersInterceptor, multi: true}]
})
export class ComponentsModule {
  constructor(private injector: Injector, private bsLocaleService: BsLocaleService) {
    ServiceLocator.injector = this.injector;
    this.bsLocaleService.use('es');//fecha en español, datepicker
  }
  static forRoot(): ModuleWithProviders<ComponentsModule> {
    return {
      ngModule: ComponentsModule,
      providers: servicesArray
    }
  }
}
